import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  CheckIcon,
  CodeIcon,
  CopyIcon,
  RotateCcwIcon,
  TriangleAlertIcon,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '~/client/src/shared/components/shadcn-ui/input-group'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  HierarchyNameDraftValidation,
  HierarchyOption,
  HierarchyRow,
} from './HierarchyPropertyConfig.types'

interface IHierarchyOptionNamePopoverProps {
  row: HierarchyRow
  getNameDraftValidation: (
    index: number,
    draft: string,
  ) => HierarchyNameDraftValidation
  onUpdateOption: (
    index: number,
    changes: Partial<Pick<HierarchyOption, 'label' | 'value'>>,
  ) => void
  onNameManuallyEdited: (value: string) => void
  onRestoreAndApply: (index: number, deletedOption: HierarchyOption) => void
}

const HierarchyOptionNamePopover: React.FC<
  IHierarchyOptionNamePopoverProps
> = ({
  row,
  getNameDraftValidation,
  onUpdateOption,
  onNameManuallyEdited,
  onRestoreAndApply,
}) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [copied, setCopied] = useState(false)
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout>>()
  const isNew = !!row.option.isNew

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        clearTimeout(copyResetTimeout.current)
        copyResetTimeout.current = setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => undefined)
  }

  useEffect(() => () => clearTimeout(copyResetTimeout.current), [])

  const validation = useMemo(
    () => (isNew ? getNameDraftValidation(row.index, draft) : null),
    [isNew, getNameDraftValidation, row.index, draft],
  )

  const isApplyDisabled =
    !validation ||
    !validation.normalized ||
    !!validation.liveCollisionLabel ||
    !!validation.archivedCollisionLabel

  const handleApply = () => {
    if (!validation || isApplyDisabled) return
    onNameManuallyEdited(row.option.value)
    onUpdateOption(row.index, { value: validation.normalized })
    setOpen(false)
  }

  const handleRestore = () => {
    if (!validation?.archivedCollisionOption) return
    onRestoreAndApply(row.index, validation.archivedCollisionOption)
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next)
        if (next) {
          setDraft(row.option.value)
        } else {
          clearTimeout(copyResetTimeout.current)
          setCopied(false)
        }
      }}
    >
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`${Localization.translator.internalName}: ${row.option.value}`}
          >
            <CodeIcon />
          </Button>
        }
      />
      <PopoverContent
        align="end"
        side="bottom"
        initialFocus={isNew ? undefined : false}
      >
        <p className="tw:m-0 tw:text-body-sm tw:text-muted-foreground">
          {Localization.translator.internalName}
        </p>
        <InputGroup>
          <InputGroupInput
            readOnly={!isNew}
            autoFocus={isNew}
            value={isNew ? draft : row.option.value}
            aria-label={Localization.translator.internalName}
            onChange={
              isNew
                ? (event: React.ChangeEvent<HTMLInputElement>) =>
                    setDraft(event.target.value)
                : undefined
            }
          />
          {!isNew && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton
                size="icon-xs"
                aria-label={
                  copied
                    ? Localization.translator.copiedToClipboard
                    : Localization.translator.copy
                }
                onClick={() => handleCopy(row.option.value)}
              >
                {copied ? <CheckIcon /> : <CopyIcon />}
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>
        {!isNew && (
          <p className="tw:m-0 tw:text-body-sm tw:text-muted-foreground">
            {Localization.translator.internalNameCannotBeChanged}
          </p>
        )}
        {isNew && validation && (
          <>
            {validation.normalized &&
              validation.normalized !== draft.trim() && (
                <p className="tw:m-0 tw:text-body-sm tw:text-muted-foreground">
                  {Localization.translator.savesAs}{' '}
                  <code className="tw:text-foreground">
                    {validation.normalized}
                  </code>
                </p>
              )}
            {validation.liveCollisionLabel &&
              !validation.archivedCollisionLabel && (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <p className="tw:m-0 tw:flex tw:items-start tw:gap-1.5 tw:text-body-sm tw:text-destructive">
                    <TriangleAlertIcon className="tw:mt-0.5 tw:size-4 tw:shrink-0" />
                    <span>
                      {Localization.translator.alreadyUsedBy(
                        validation.liveCollisionLabel,
                      )}
                    </span>
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="tw:self-start"
                    onClick={() => setDraft(validation.dedupedValue)}
                  >
                    {Localization.translator.useValue(validation.dedupedValue)}
                  </Button>
                </div>
              )}
            {validation.archivedCollisionLabel && (
              <div className="tw:flex tw:flex-col tw:gap-2">
                <p className="tw:m-0 tw:flex tw:items-start tw:gap-1.5 tw:text-body-sm tw:text-muted-foreground">
                  <TriangleAlertIcon className="tw:mt-0.5 tw:size-4 tw:shrink-0" />
                  <span>
                    {Localization.translator.matchesDeletedOptionWillRestore(
                      validation.archivedCollisionLabel,
                    )}
                  </span>
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="tw:self-start"
                  onClick={handleRestore}
                >
                  <RotateCcwIcon data-icon="inline-start" />
                  {Localization.translator.restoreAndApply}
                </Button>
              </div>
            )}
            {!validation.archivedCollisionLabel && (
              <div className="tw:flex tw:justify-end tw:gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                >
                  {Localization.translator.cancel}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  disabled={isApplyDisabled}
                  onClick={handleApply}
                >
                  {Localization.translator.apply}
                </Button>
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default HierarchyOptionNamePopover
